import { Amplify } from '@aws-amplify/core'
import {
  signUp,
  signIn,
  confirmSignIn,
  updateUserAttributes,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
  decodeJWT,
} from '@aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from '@aws-amplify/auth/cognito'
import {
  PublicClientApplication,
  type AuthenticationResult,
} from '@azure/msal-browser'
import type { User } from '@/types/auth'

// ---------------------------------------------------------------------------
// MSAL (Microsoft) configuration — initialized once
// ---------------------------------------------------------------------------

const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID || '',
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_MICROSOFT_TENANT_ID || 'common'}`,
    redirectUri: `${window.location.origin}/auth`,
  },
}

const msalInstance = new PublicClientApplication(msalConfig)

const msalInitPromise = msalInstance.initialize()

// ---------------------------------------------------------------------------
// Amplify (Cognito) configuration — no hosted-UI / OAuth needed
// ---------------------------------------------------------------------------

export function configureAuth() {
  const config = {
    Auth: {
      Cognito: {
        userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
        userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
      },
    },
  }

  console.debug('[auth] Amplify.configure', config)

  cognitoUserPoolsTokenProvider.setAuthConfig(config.Auth)
  Amplify.configure(config, {
    Auth: { tokenProvider: cognitoUserPoolsTokenProvider },
  })
}

// ---------------------------------------------------------------------------
// Sign-in: MSAL redirect → Cognito custom auth
// ---------------------------------------------------------------------------

/**
 * Kick off the Microsoft login redirect.
 * After the user authenticates, MSAL will redirect back to the app and
 * `handleMicrosoftCallback` picks up the result.
 */
export async function signInWithMicrosoft(): Promise<void> {
  await msalInitPromise
  await msalInstance.loginRedirect({
    scopes: ['openid', 'email', 'profile', 'User.Read'],
  })
}

/**
 * Call on every app mount. If the page loaded as a result of an MSAL
 * redirect, this finishes the flow: it extracts the Microsoft ID token
 * and creates/authenticates the Cognito user via custom auth.
 *
 * Returns `true` if a redirect was handled (i.e. user is now signed in).
 */
export async function handleMicrosoftCallback(): Promise<boolean> {
  await msalInitPromise
  let result: AuthenticationResult | null
  try {
    result = await msalInstance.handleRedirectPromise()
  } catch (error) {
    console.error('[auth] MSAL redirect handling failed', error)
    return false
  }

  if (!result?.idToken) return false

  const msIdToken = result.idToken
  let msAccessToken = result.accessToken
  const email = result.account?.username
  if (!email) {
    console.error('[auth] Microsoft token missing username/email')
    return false
  }

  if (!msAccessToken && result.account) {
    try {
      const tokenResult = await msalInstance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: result.account,
      })
      msAccessToken = tokenResult.accessToken
    } catch (err) {
      console.warn('[auth] Failed to acquire Graph access token silently', err)
    }
  }

  console.debug('[auth] Microsoft token received, starting Cognito custom auth')
  await cognitoCustomAuth(email, msIdToken, msAccessToken)
  return true
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class TenantNotActivatedError extends Error {
  constructor() {
    super(
      'The company associated with this account has not been activated in the FrontFlow Cloud Platform. Please contact support@frontflow.com for help.',
    )
    this.name = 'TenantNotActivatedError'
  }
}

// ---------------------------------------------------------------------------
// Cognito custom-auth helpers
// ---------------------------------------------------------------------------

function generateRandomPassword(): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lower = 'abcdefghijklmnopqrstuvwxyz'
  const digits = '0123456789'
  const symbols = '!@#$%^&*'
  const all = upper + lower + digits + symbols

  const pick = (pool: string) => {
    const buf = new Uint8Array(1)
    crypto.getRandomValues(buf)
    return pool[buf[0] % pool.length]
  }

  // Guarantee at least one from each required category
  const guaranteed = [pick(upper), pick(lower), pick(digits), pick(symbols)]

  const rest = new Uint8Array(20)
  crypto.getRandomValues(rest)
  const fill = Array.from(rest, (b) => all[b % all.length])

  // Shuffle so the guaranteed chars aren't always at the front
  const combined = [...guaranteed, ...fill]
  for (let i = combined.length - 1; i > 0; i--) {
    const buf = new Uint8Array(1)
    crypto.getRandomValues(buf)
    const j = buf[0] % (i + 1)
    ;[combined[i], combined[j]] = [combined[j], combined[i]]
  }

  return combined.join('')
}

/**
 * 1. signUp (ignore if user exists)
 * 2. signIn with CUSTOM_WITHOUT_SRP
 * 3. confirmSignIn with the Microsoft ID token as the challenge answer
 */
async function cognitoCustomAuth(
  email: string,
  msIdToken: string,
  msAccessToken?: string,
): Promise<void> {
  // --- Step 1: ensure Cognito user exists ---
  try {
    await signUp({
      username: email,
      password: generateRandomPassword(),
      options: {
        userAttributes: {
          email,
          'custom:microsoft_token': msIdToken,
        },
      },
    })
    console.debug('[auth] Cognito user created')
  } catch (err: unknown) {
    if (
      err instanceof Error &&
      err.name === 'UsernameExistsException'
    ) {
      console.debug('[auth] Cognito user already exists, continuing')
    } else if (
      err instanceof Error &&
      err.name === 'UserLambdaValidationException' &&
      err.message.includes('tenant')
    ) {
      throw new TenantNotActivatedError()
    } else {
      throw err
    }
  }

  // --- Step 2: initiate custom auth challenge ---
  // If a previous Cognito session is still alive (e.g. stale session from a
  // prior login that is missing custom:tenant_id), Amplify refuses to start a
  // new sign-in flow and throws UserAlreadyAuthenticatedException.
  // Sign out the stale session first so the fresh Microsoft token can go through
  // the Lambda and have the correct custom:tenant_id written into the new token.
  let signInResult
  try {
    signInResult = await signIn({
      username: email,
      options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
    })
  } catch (err) {
    if (err instanceof Error && err.name === 'UserAlreadyAuthenticatedException') {
      console.debug('[auth] stale Cognito session detected, signing out and retrying')
      await amplifySignOut({ global: false })
      signInResult = await signIn({
        username: email,
        options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
      })
    } else {
      throw err
    }
  }

  console.debug('[auth] signIn result', signInResult)

  if (
    signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
  ) {
    // --- Step 3: respond with Microsoft token ---
    const confirmResult = await confirmSignIn({
      challengeResponse: JSON.stringify({
        id_token: msIdToken,
        access_token: msAccessToken || '',
      }),
    })
    console.debug('[auth] confirmSignIn result', confirmResult)

    // --- Step 4: clear the Microsoft token from Cognito now that auth is complete ---
    try {
      await updateUserAttributes({
        userAttributes: { 'custom:microsoft_token': 'cleared' },
      })
      console.debug('[auth] cleared custom:microsoft_token from Cognito user')
    } catch (err) {
      // Non-fatal: log but don't block the user from continuing
      console.warn('[auth] failed to clear custom:microsoft_token', err)
    }
  }
}

// ---------------------------------------------------------------------------
// Sign-out
// ---------------------------------------------------------------------------

export async function signOut(): Promise<void> {
  await msalInitPromise

  let signOutError: unknown
  try {
    await amplifySignOut({ global: false })
  } catch (error) {
    signOutError = error
  }

  try {
    const accounts = msalInstance.getAllAccounts()
    for (const account of accounts) {
      await msalInstance.clearCache({ account })
    }
    msalInstance.setActiveAccount(null)
  } catch (error) {
    console.warn('[auth] Failed to clear Microsoft session cache', error)
  }

  if (signOutError) {
    console.error('Sign out error:', signOutError)
    throw signOutError
  }
}

// ---------------------------------------------------------------------------
// Session helpers (unchanged — they read the Cognito session)
// ---------------------------------------------------------------------------

export async function getUser(): Promise<User | null> {
  try {
    const cognitoUser = await getCurrentUser()
    let name: string | undefined
    let email: string | undefined
    let given_name: string | undefined
    let family_name: string | undefined
    let tenantId: string | undefined
    try {
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken?.toString()
      if (idToken) {
        const payload = decodeJWT(idToken).payload as {
          name?: string
          email?: string
          given_name?: string
          family_name?: string
          'custom:tenant_id'?: string
        }
        name = payload.name
        email = payload.email
        given_name = payload.given_name
        family_name = payload.family_name
        tenantId = payload['custom:tenant_id']
      }
    } catch {
      // Ignore; name stays undefined
    }
    return {
      id: cognitoUser.userId,
      email: email ?? '',
      name: name ?? cognitoUser.username,
      given_name: given_name ?? '',
      family_name: family_name ?? '',
      tenantId: tenantId ?? '',
    }
  } catch {
    return null
  }
}

export async function getAccessToken(forceRefresh = false): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh })
    return session.tokens?.accessToken?.toString() || null
  } catch (error) {
    console.error('Error fetching access token:', error)
    return null
  }
}

export async function getIdToken(forceRefresh = false): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh })
    return session.tokens?.idToken?.toString() || null
  } catch (error) {
    console.error('Error fetching ID token:', error)
    return null
  }
}

export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser()
    return true
  } catch {
    return false
  }
}
